import {
  Box,
  CircularProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import isEmpty from "lodash/isEmpty";

import { useEffect, useState } from "react";
import TableNoDataOverlay from "../../../common/components/table/TableNoDataOverlay";
import { useDeploymentHealthTableRows } from "../hooks/useDeploymentHealthTableRows";
import DeploymentHealthTableBodyRow from "./table/DeploymentHealthTableBodyRow";
import DeploymentHealthTableHeadRow from "./table/DeploymentHealthTableHeadRow";

const DeploymentHealthTable = (): JSX.Element => {
  const { data, loading, fetchingMore } = useDeploymentHealthTableRows();

  const computeOverallHealthPercent = (): string => {
    const rows = data ?? [];
    const isGrey = (row: (typeof rows)[number]): boolean => {
      const nodeAllExpectedOffline = row.nodes.every(
        n => n.isExpectedOnline === false
      );
      const deviceAllExpectedOffline = row.devices.every(
        d => d.isExpectedOnline === false
      );
      const serviceAllExpectedOffline = row.services.every(
        s => s.isExpectedOnline === false
      );

      return (
        nodeAllExpectedOffline &&
        deviceAllExpectedOffline &&
        serviceAllExpectedOffline
      );
    };

    // If all locations are grey (no operating facilities), return special messaging
    const allGrey = rows.length > 0 && rows.every(isGrey);

    if (allGrey) {
      return "No operating facilities";
    }

    const considered = rows.filter(r => !isGrey(r));
    const green = considered.filter(r => r.locationStatus).length;
    const total = considered.length; // green + red

    if (total === 0) return "0%";

    return `${Math.round((green / total) * 100)}%`;
  };

  const [facilitiesHealthPercent, setFacilitiesHealthPercent] =
    useState<string>("Loading ...");

  const overallTooltip =
    "Calculated as: among facilities with at least one expected-online node/device/service, the percent that are healthy (green).\n" +
    "A facility is healthy when all expected-online nodes, devices, and services are online.\n" +
    "Facilities where everything is expected offline are excluded (grey).\n" +
    "Any nodes, devices, services not managed by Cleanconnect will not be part of this calculation";

  useEffect(() => {
    if (!loading && !fetchingMore) {
      setFacilitiesHealthPercent(computeOverallHealthPercent());
    } else {
      setFacilitiesHealthPercent("");
    }
  }, [loading, fetchingMore, data]);

  return (
    <Paper>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          pt: 2,
          gap: 2,
        }}
      >
        <Tooltip
          title={<Box sx={{ whiteSpace: "pre-line" }}>{overallTooltip}</Box>}
          arrow
          placement="right"
        >
          <Typography variant="h6" sx={{ cursor: "help" }}>
            Overall Facility Health:{" "}
            {!loading && !fetchingMore && facilitiesHealthPercent}
          </Typography>
        </Tooltip>
        {(loading || fetchingMore) && <CircularProgress size={20} />}
      </Box>
      <Table>
        <DeploymentHealthTableHeadRow />
        <TableBody>
          {data &&
            !loading &&
            data.map((row): JSX.Element => {
              return (
                <DeploymentHealthTableBodyRow key={row.locationId} row={row} />
              );
            })}

          {loading && (
            <TableRow>
              <TableCell colSpan={6}>
                {[...Array(6)].map(
                  (_, index): JSX.Element => (
                    <Skeleton
                      key={index}
                      variant="rectangular"
                      sx={{ my: 3 }}
                    />
                  )
                )}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {isEmpty(data) && !loading && <TableNoDataOverlay />}
      {fetchingMore && (
        <Box
          sx={{ display: "flex", flexDirection: "column", py: 1, padding: 2 }}
        >
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </Box>
      )}
    </Paper>
  );
};

export default DeploymentHealthTable;
